import {
  DEFAULT_BATCH_ANALYSIS_MAX_BATCHES,
  DEFAULT_BATCH_ANALYSIS_MAX_MATCHED_IDS,
  DEFAULT_BATCH_ANALYSIS_MAX_REASONS,
  DEFAULT_BATCH_SIZE,
  MAX_BATCH_ANALYSIS_MAX_BATCHES,
  MAX_BATCH_ANALYSIS_MAX_MATCHED_IDS,
  MAX_BATCH_ANALYSIS_MAX_REASONS,
  MAX_BATCH_SIZE,
} from "../../agentResourceQuery";
import type { ToolGroupDefinition } from "../../AgentToolsService";
import { AgentToolsService } from "../../AgentToolsService";

/**
 * Register Tier 3 semantic resource analysis tools.
 * Hidden from initial context; fetch via get_tool_info with group="resource_analysis_tools".
 */
export function registerResourceAnalysisTools(): Record<string, ToolGroupDefinition> {
  return {
    resource_analysis_tools: {
      name: "resource_analysis_tools",
      description:
        'Tools for AI-assisted semantic analysis over large resource sets. Use get_tool_info with group="resource_analysis_tools" when you need judgment-based questions such as "which players seem inactive based on notes?" Prefer scan_resource for deterministic counts, IDs, ranges, and structural filters.',
      hidden: true,
      tools: {
        analyze_resource_batches: {
          name: "analyze_resource_batches",
          description:
            "Run bounded AI analysis over filtered resource rows in batches. Use for semantic or judgment-based questions over many records. Requires explicit fields (always includes id). Returns one compact merged result with matched IDs, optional reasons, and batch summaries. Use scan_resource for deterministic counts or ID lists.",
          requiredParameters: ["resource", "question", "fields"],
          parametersSchema: {
            resource: {
              type: "string",
              description: "Registered resource name to analyze. Use exact names from available resources.",
            },
            question: {
              type: "string",
              description:
                "The semantic question to answer for each batch, e.g. which records look inactive based on notes.",
            },
            filter: {
              type: "object",
              description:
                "Optional filter criteria. Supports eq, partial, gte, gt, lte, lt, in, and exists operators (same as scan_resource).",
              additionalProperties: true,
            },
            fields: {
              type: "array",
              items: { type: "string" },
              description:
                "Required field names to send to the batch analyzer. id is always included. Do not request every available field.",
            },
            relationships: {
              type: "object",
              description:
                "Optional related resources to include, with arrays of fields to include for each.",
              additionalProperties: true,
            },
            batchSize: {
              type: "number",
              description: `Optional rows per batch (default ${DEFAULT_BATCH_SIZE}, max ${MAX_BATCH_SIZE}).`,
            },
            maxBatches: {
              type: "number",
              description: `Optional maximum number of one-shot batch calls (default ${DEFAULT_BATCH_ANALYSIS_MAX_BATCHES}, max ${MAX_BATCH_ANALYSIS_MAX_BATCHES}).`,
            },
            maxMatchedIds: {
              type: "number",
              description: `Optional cap on returned matched IDs (default ${DEFAULT_BATCH_ANALYSIS_MAX_MATCHED_IDS}, max ${MAX_BATCH_ANALYSIS_MAX_MATCHED_IDS}).`,
            },
            maxReasons: {
              type: "number",
              description: `Optional cap on returned reasons (default ${DEFAULT_BATCH_ANALYSIS_MAX_REASONS}, max ${MAX_BATCH_ANALYSIS_MAX_REASONS}).`,
            },
          },
          parameters: {},
          execute: async (parameters: Record<string, unknown>) => {
            return AgentToolsService.getInstance().executeResourceBatchAnalysis(parameters as any);
          },
        },
      },
    },
  };
}
